import React, { useState, useEffect, useContext } from "react";
import { Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function ManageFAQ() {
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "FAQ", path: "/superadmin/faq", isLast: true },
  ];

  const createButtons = [
    {
      text: "Add FAQ",
      onClick: () => setIsCreateModalOpen(true),
    },
  ];

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    displayOrder: "",
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    question: "",
    answer: "",
    displayOrder: "",
  });

  const [formError, setFormError] = useState("");

  // Fetch all FAQs
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/faq/get-all`,
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        setFaqData(data.data || data);
      } else {
        throw new Error("Failed to fetch FAQs");
      }
    } catch (err) {
      setError(err.message);
      swalError("Error", "Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  // Delete FAQ
  const handleDelete = async (id, question) => {
    const result = await swalConfirm(
      "Delete FAQ",
      `Are you sure you want to delete this FAQ?\n"${question}"`,
      "Yes, delete it!",
      "No, Cancel"
    );
    if (result.isConfirmed) {
      try {
        setIsDeleting(true);
        setDeletingId(id);
        const response = await fetchWithAuth(
          `${serverUrl}/api/faq/delete/${id}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (response.ok) {
          swalSuccess("Deleted", "FAQ deleted successfully");
          fetchFAQs();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete FAQ");
        }
      } catch (err) {
        swalError("Error", err.message);
      } finally {
        setIsDeleting(false);
        setDeletingId(null);
      }
    }
  };

  // Handle input changes for create form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setIsEditChanged(true);
  };

  // Create FAQ
  const handleCreateFAQ = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.question.trim()) {
      setFormError("Question is required");
      return;
    }
    if (!formData.answer.trim()) {
      setFormError("Answer is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetchWithAuth(`${serverUrl}/api/faq/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: formData.question.trim(),
          answer: formData.answer.trim(),
          displayOrder: formData.displayOrder !== "" ? Number(formData.displayOrder) : null,
        }),
      });

      if (response.ok) {
        swalSuccess("Created", "FAQ created successfully");
        setFormData({ question: "", answer: "", displayOrder: "" });
        setIsCreateModalOpen(false);
        fetchFAQs();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create FAQ");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit FAQ
  const handleEditFAQ = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!editFormData.question.trim()) {
      setFormError("Question is required");
      return;
    }
    if (!editFormData.answer.trim()) {
      setFormError("Answer is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetchWithAuth(
        `${serverUrl}/api/faq/update/${editFormData.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: editFormData.question.trim(),
            answer: editFormData.answer.trim(),
            displayOrder: editFormData.displayOrder !== "" ? Number(editFormData.displayOrder) : null,
          }),
        }
      );

      if (response.ok) {
        swalSuccess("Updated", "FAQ updated successfully");
        setIsEditModalOpen(false);
        fetchFAQs();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update FAQ");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
      setIsEditChanged(false);
    }
  };

  // Open edit modal
  const openEditModal = (faq) => {
    setEditFormData({
      id: faq._id,
      question: faq.question,
      answer: faq.answer,
      displayOrder: faq.displayOrder ?? "",
    });
    setIsEditChanged(false);
    setFormError("");
    setIsEditModalOpen(true);
  };

  // Columns for table
  const columns = [
    {
      key: "displayOrder",
      label: "Order",
      sortable: true,
      render: (row) => <span>{row.displayOrder ?? "-"}</span>,
    },
    {
      key: "question",
      label: "Question",
      sortable: true,
      render: (row) => (
        <span title={row.question}>
          {row.question.length > 80
            ? row.question.slice(0, 80) + "…"
            : row.question}
        </span>
      ),
    },
    {
      key: "answer",
      label: "Answer",
      sortable: false,
      render: (row) => (
        <span title={row.answer}>
          {row.answer.length > 100
            ? row.answer.slice(0, 100) + "…"
            : row.answer}
        </span>
      ),
    },
    {
      key: "edit",
      label: "Edit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          onClick={() => openEditModal(row)}
          title="Edit FAQ"
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    {
      key: "delete",
      label: "Delete",
      sortable: false,
      render: (row) => (
        <button
          className="delete-btn"
          onClick={() => handleDelete(row._id, row.question)}
          disabled={isDeleting && deletingId === row._id}
          title="Delete FAQ"
        >
          {isDeleting && deletingId === row._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash"></i>
          )}
        </button>
      ),
    },
  ];

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/" />;
  }

  if (error && faqData.length === 0) {
    return (
      <div>
        <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
        <div className="container" style={{ marginTop: "20px" }}>
          <h2 style={{ color: "red" }}>Error: {error}</h2>
          <button onClick={() => fetchFAQs()} className="btn-submit">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
      <div className="container" style={{ marginTop: "20px" }}>
        {loading ? (
          <Loader />
        ) : faqData.length > 0 ? (
          <Table
            columns={columns}
            data={[...faqData].sort((a, b) => {
              const aOrder = a.displayOrder ?? -1;
              const bOrder = b.displayOrder ?? -1;
              return aOrder - bOrder;
            })}
            caption="FAQs"
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            <p>No FAQs found. Add one to get started!</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
        title="Add New FAQ"
      >
        <form onSubmit={handleCreateFAQ}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label htmlFor="faq-create-question">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Question
            </label>
            <input
              id="faq-create-question"
              type="text"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              placeholder="Enter the FAQ question"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="faq-create-answer">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Answer
            </label>
            <textarea
              id="faq-create-answer"
              name="answer"
              value={formData.answer}
              onChange={handleInputChange}
              placeholder="Enter the FAQ answer"
              rows="5"
              style={{ height: "8rem" }}
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="faq-create-displayOrder">
              Display Order
            </label>
            <input
              id="faq-create-displayOrder"
              type="number"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              placeholder="Enter display order (e.g., 1)"
            />
          </div>

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader
                  style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
                />
              ) : (
                <>
                  <i className="fa-solid fa-plus"></i> Add FAQ
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        title="Edit FAQ"
      >
        <form onSubmit={handleEditFAQ}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label htmlFor="faq-edit-question">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Question
            </label>
            <input
              id="faq-edit-question"
              type="text"
              name="question"
              value={editFormData.question}
              onChange={handleEditInputChange}
              placeholder="Enter the FAQ question"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="faq-edit-answer">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Answer
            </label>
            <textarea
              id="faq-edit-answer"
              name="answer"
              value={editFormData.answer}
              onChange={handleEditInputChange}
              placeholder="Enter the FAQ answer"
              rows="5"
              style={{ height: "8rem" }}
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="faq-edit-displayOrder">
              Display Order
            </label>
            <input
              id="faq-edit-displayOrder"
              type="number"
              name="displayOrder"
              value={editFormData.displayOrder}
              onChange={handleEditInputChange}
              placeholder="Enter display order (e.g., 1)"
            />
          </div>

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || !isEditChanged}
            >
              {isSubmitting ? (
                <Loader
                  style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
                />
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk"></i> Update FAQ
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
